import { Injectable, Inject, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { TenantService } from '../tenant/tenant';
import { API_BASE_URL } from './api-config';
import { environment } from '../../../environments/environment';
import { FlatWebsiteSettings } from '../models/website.model';

// Create a unique identifier to store the JSON data in the DOM
const SITE_SETTINGS_KEY = makeStateKey<FlatWebsiteSettings>('site-settings');

@Injectable({
    providedIn: 'root'
})
export class TenantConfigService {
    // This public property will hold the configuration data for your components to read
    public settings: FlatWebsiteSettings | null = null;

    constructor(
        private http: HttpClient,
        private transferState: TransferState,
        @Inject(PLATFORM_ID) private platformId: Object,
        private tenantService: TenantService,
        @Inject(API_BASE_URL) private apiBaseUrl: string,
        @Inject(DOCUMENT) private document: Document
    ) { }

    async loadSettings(): Promise<void> {
        // Retrieve the active domain using the detective service we built
        let domain = this.tenantService.getDomain();

        // Fallback to configured domain if running locally/localhost or domain is empty
        if (!domain || domain === 'localhost' || domain === '127.0.0.1' || domain.includes("efrx")) {
            domain = environment.fallbackDomain;
        }

        // 1. BROWSER BEHAVIOR: Check if the server already attached the data to the HTML
        if (this.transferState.hasKey(SITE_SETTINGS_KEY)) {
            this.settings = this.transferState.get(SITE_SETTINGS_KEY, null);
            this.transferState.remove(SITE_SETTINGS_KEY); // Clean up memory immediately
            this.applyTheme(this.settings?.theme);
            this.updateFavicon(this.settings?.favicon);
            return;
        }

        // 2. SERVER BEHAVIOR (or Client fallback if no SSR occurred): Make the API Call
        try {
            // Execute the GET request using the centralized API base URL
            this.settings = await firstValueFrom(
                // We hit the Express proxy we just built!
                this.http.get<FlatWebsiteSettings>(`${this.apiBaseUrl}/internal-api/settings/${domain}`).pipe(
                    timeout(5000), // Fail fast if it takes more than 5 seconds
                    catchError(() => of({ title: 'Default Storefront', theme: {}, eroor: true } as FlatWebsiteSettings))
                )
            );

            this.applyTheme(this.settings?.theme);
            this.updateFavicon(this.settings?.favicon);

            // 3. SERVER BEHAVIOR: Save the API response into TransferState so the browser can see it
            if (isPlatformServer(this.platformId)) {
                this.transferState.set(SITE_SETTINGS_KEY, this.settings);

            }
        } catch (error) {
            console.error(`Failed to load website settings for domain: ${domain}`, error);
            // Optional: Set default fallback settings here in case the API goes down
        }
    }

    public updateFavicon(faviconUrl: string | undefined): void {
        if (!faviconUrl) return;
        try {
            let link: HTMLLinkElement | null = this.document.querySelector("link[rel*='icon']");
            if (!link) {
                link = this.document.createElement('link');
                link.rel = 'icon';
                this.document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = faviconUrl;
        } catch (e) {
            console.error('Failed to update favicon link in head:', e);
        }
    }

    public applyTheme(themeInput: string | Record<string, string> | undefined | null): void {
        if (!themeInput) return;

        let themeObj: Record<string, string> | null = null;
        if (typeof themeInput === 'string') {
            const trimmed = themeInput.trim();
            if (!trimmed) return;

            try {
                themeObj = JSON.parse(trimmed);
            } catch {
                try {
                    // Fallback evaluation for JS object literal strings (e.g. single quotes or unescaped quotes)
                    themeObj = new Function(`return (${trimmed})`)();
                } catch (error) {
                    console.error('Failed to parse theme string:', error, trimmed);
                }
            }
        } else if (typeof themeInput === 'object') {
            themeObj = themeInput;
        }

        if (themeObj && typeof document !== 'undefined') {
            const root = document.documentElement;
            Object.entries(themeObj).forEach(([key, value]) => {
                if (key && value && typeof value === 'string') {
                    root.style.setProperty(key.trim(), value.trim());
                }
            });
        }
    }
}